/**
 * Platform uygulaması kataloğu — kullanıcı bazlı erişim/lisans kontrolü.
 * id = route emoji'si (uygulamalar.routes data.animasyonTag/simge ile eşleşir).
 * mod: 'herkes' (serbest) | 'kisitli' (sadece süper + admin'in izin verdiği) | 'ucretli' (izin/abonelik gerekir).
 * Kullanıcı izinleri goUyeler.veri.uygulamalar[] (emoji id listesi); süper yönetici hepsine erişir.
 */
export type UygulamaErisimModu = 'herkes' | 'kisitli' | 'ucretli';

/** Uygulama içi katma değerli alt özellik — ayrı ek ücretlendirilir. */
export interface IAltOzellik {
  id: string;          // uygulama içinde benzersiz
  baslik: string;
  ekFiyat: number;     // ₺/ay (uygulama baz ücretine EK)
}

export interface IUygulamaTanim {
  id: string;          // route emoji
  baslik: string;
  simge: string;
  mod: UygulamaErisimModu;
  aylikFiyat?: number; // ₺/ay (ucretli baz)
  altOzellikler?: IAltOzellik[];
}

/** Erişimi sınırlı uygulamalar. Katalogda OLMAYAN bir route varsayılan 'herkes' kabul edilir. */
export const UYGULAMA_KATALOG: IUygulamaTanim[] = [
  // 🌌 3D = sadece ERİŞİM bileti (ücretli). 3D'nin İÇERİĞİ (Görüntüleme/Ölçüm/CAM...) "Lisans (Workbench)" sekmesinden — burada alt özellik YOK (örtüşme önlendi).
  { id: '🌌', baslik: 'FikoYT 3D (erişim)', simge: '🌌', mod: 'ucretli', aylikFiyat: 500 },
  {
    id: '✂️', baslik: 'Strafor Kesici', simge: '✂️', mod: 'ucretli', aylikFiyat: 1000,
    altOzellikler: [
      { id: 'nesting', baslik: 'Otomatik Yerleşim (Nesting)', ekFiyat: 800 },
      { id: 'dxf', baslik: 'DXF/DWG İçe Aktarma', ekFiyat: 400 },
      { id: 'simulasyon', baslik: 'Kesim Simülasyonu', ekFiyat: 500 },
      { id: 'coklu_parca', baslik: 'Çoklu Parça', ekFiyat: 300 },
      { id: 'gcode', baslik: 'G-code Dışa Aktarma', ekFiyat: 600 },
    ],
  },
  {
    id: '📦', baslik: 'Parça Takip', simge: '📦', mod: 'ucretli', aylikFiyat: 3000,
    altOzellikler: [
      { id: 'musteri_portal', baslik: 'Müşteri Portalı (link/QR/onay)', ekFiyat: 800 },
      { id: 'maliyet', baslik: 'Maliyet/Kâr Analizi', ekFiyat: 400 },
      { id: 'dosya', baslik: 'STP/Foto Dosya Saklama', ekFiyat: 400 },
    ],
  },
  {
    id: '📟', baslik: 'HMI Pano', simge: '📟', mod: 'ucretli', aylikFiyat: 1200,
    altOzellikler: [
      { id: 'dashboard', baslik: 'Dashboard & Widget Tasarımcı', ekFiyat: 800 },
      { id: 'pdks', baslik: 'PDKS / Puantaj (Kart Okuyucu)', ekFiyat: 600 },
      { id: 'posta', baslik: 'Posta / Mail Kutusu', ekFiyat: 400 },
      { id: 'uretim', baslik: 'Üretim Takip Paneli', ekFiyat: 500 },
      { id: 'satis', baslik: 'Satış Paneli', ekFiyat: 500 },
      { id: 'projeler', baslik: 'Proje Yönetimi', ekFiyat: 400 },
      { id: 'yonetim', baslik: 'Yönetim / Personel Kayıt', ekFiyat: 500 },
    ],
  },
  {
    id: '📢', baslik: 'Paylaşım', simge: '📢', mod: 'ucretli', aylikFiyat: 400,
    altOzellikler: [
      // ── Medya & İçerik ──
      { id: 'ses_kayit', baslik: 'Ses Kaydı & Oynatma', ekFiyat: 300 },
      { id: 'whisper', baslik: 'Sesi Yazıya Çevir (Whisper AI)', ekFiyat: 400 },
      { id: 'video', baslik: 'Video Yükleme', ekFiyat: 300 },
      { id: 'youtube', baslik: 'YouTube Embed', ekFiyat: 200 },
      { id: 'ai_gorsel', baslik: 'Yapay Zeka Görsel Üretimi', ekFiyat: 600 },
      { id: 'yuz_etiket', baslik: 'Resimde Yüz Etiketleme', ekFiyat: 300 },
      // ── Tasarım & Şablon ──
      { id: 'tasarim_editor', baslik: 'Tasarım Editörü (Sürükle-Bırak)', ekFiyat: 1200 },
      { id: 'sablon_olustur', baslik: 'Şablon Oluşturma & Kaydetme', ekFiyat: 500 },
      { id: 'parametreli', baslik: 'Parametreli / Dinamik İçerik', ekFiyat: 600 },
      { id: 'sistem_sablon', baslik: 'Sistem Şablonları Erişimi', ekFiyat: 300 },
      // ── Gizlilik & Hedefleme ──
      { id: 'gelismis_gizlilik', baslik: 'Gelişmiş Gizlilik (Grup/Liste/Hariç)', ekFiyat: 400 },
      { id: 'icerik_sifre', baslik: 'İçerik Şifre Kilidi', ekFiyat: 300 },
      { id: 'konum', baslik: 'Konum Paylaşımı (Harita)', ekFiyat: 200 },
      { id: 'etiket_editor', baslik: 'Özel Etiket Editörü (Renkli)', ekFiyat: 200 },
      // ── İş Akışı & Kurumsal ──
      { id: 'firma_custom_kart', baslik: 'Firmaya Özel Custom Kartlar (Kurumsal)', ekFiyat: 2500 },
      { id: 'mention', baslik: 'Mention Bildirimleri (@kullanıcı)', ekFiyat: 200 },
      { id: 'canli_yayin', baslik: 'Canlı Yayın (Gerçek Zamanlı)', ekFiyat: 300 },
      { id: 'kart_klon', baslik: 'Kart Klonlama & Bayrak Özelleştirme', ekFiyat: 200 },
    ],
  },
  {
    id: '🤖', baslik: 'AI Asistan (Kendi AI’ını Bağla)', simge: '🤖', mod: 'ucretli', aylikFiyat: 2000,
    altOzellikler: [
      { id: 'veri_sorgu', baslik: 'Firma Verisi Sorgulama (DB üzerinde AI)', ekFiyat: 1200 },
      { id: 'kart_otomasyon', baslik: 'Otomatik Kart / Paylaşım Üretimi', ekFiyat: 800 },
      { id: 'mcp_ozel', baslik: 'Firmaya Özel MCP / API Entegrasyonu (Kurumsal)', ekFiyat: 3000 },
      { id: 'coklu_model', baslik: 'Çoklu Model (GPT + Claude + Gemini)', ekFiyat: 600 },
      { id: 'sesli_komut', baslik: 'Sesli Komut & Yanıt', ekFiyat: 500 },
      { id: 'otomasyon_akis', baslik: 'Otomasyon Akışları (Çok Adımlı Görev)', ekFiyat: 1800 },
      { id: 'bilgi_tabani', baslik: 'Firma Bilgi Tabanı (RAG / Doküman)', ekFiyat: 1200 },
    ],
  },
  { id: '🌳', baslik: 'Soy Ağacı', simge: '🌳', mod: 'kisitli' },
  { id: '⚔️', baslik: 'Fikorita (Oyun)', simge: '⚔️', mod: 'kisitli' },
  {
    id: '🗺️', baslik: 'Ağ Haritam', simge: '🗺️', mod: 'ucretli', aylikFiyat: 300,
    altOzellikler: [
      { id: 'sinirsiz_kayit', baslik: 'Sınırsız Kayıt (Pro)', ekFiyat: 300 },
      { id: 'ozel_tip', baslik: 'Sınırsız Özel Tür/Alan', ekFiyat: 200 },
      { id: 'foto', baslik: 'Fotoğraf Yükleme', ekFiyat: 150 },
      { id: 'ag_gorunum', baslik: 'Ağ (Graf) Görünümü', ekFiyat: 200 },
      { id: 'zaman_cizelgesi', baslik: 'Zaman Çizelgesi / Oynatma', ekFiyat: 200 },
    ],
  },
];

export const UYGULAMA_BUL = (id: string): IUygulamaTanim | undefined => UYGULAMA_KATALOG.find(u => u.id === id);
/** "uygId:altId" anahtarı (alt özellik entitlement saklama). */
export const ALT_OZ_ANAHTAR = (uygId: string, altId: string) => `${uygId}:${altId}`;
